import React from "react";

// eslint-disable-next-line no-restricted-imports
import { Box, Collapse, TableCell, TableCellProps, TableRow, useTheme } from "@mui/material";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { Row } from "@tanstack/react-table";

import { HoverRow } from "./HoverRow";
import { MinimalTableExpandCell } from "./MinimalTableComponents";

type CollapsibleContextType = { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> };
const CollapsibleContext = React.createContext<CollapsibleContextType>({
    open: false,
    setOpen: () => null,
});

export const CollapsibleRow = ({ children }: React.PropsWithChildren<object>) => {
    const [open, setOpen] = React.useState(false);

    return <CollapsibleContext.Provider value={{ open, setOpen }}>{children}</CollapsibleContext.Provider>;
};

type CollapsibleSummaryProps<T> = React.PropsWithChildren<TableCellProps> & {
    row: Row<T>;
    disabled?: boolean;
};
export const CollapsibleSummary = <T,>({ children, disabled, row, ...props }: CollapsibleSummaryProps<T>) => {
    const context = React.useContext(CollapsibleContext);

    if (!context) {
        throw new Error("CollapsibleSummary have to be use inside CollapsibleContext");
    }

    return (
        <HoverRow {...row}>
            <MinimalTableExpandCell {...props}>
                <ExpandToggleButton
                    expanded={context.open}
                    onClick={() => context.setOpen((prev) => !prev)}
                    disabled={disabled}
                />
            </MinimalTableExpandCell>
            {children}
        </HoverRow>
    );
};

type MinimalTableDetailsRowProps = React.PropsWithChildren<{
    colSpan: number;
    className?: string;
    cellClassName?: string;
    cellStyle?: React.CSSProperties;
}>;

/**
 * A full-width row under a table row, holding that row's details. The cell has no padding and no border, so the
 * content (usually a `Collapse`) decides its own spacing.
 */
export const MinimalTableDetailsRow = ({
    colSpan,
    className,
    cellClassName,
    cellStyle,
    children,
}: MinimalTableDetailsRowProps) => (
    <TableRow className={className}>
        <TableCell colSpan={colSpan} className={cellClassName} style={{ padding: 0, borderBottom: "none", ...cellStyle }}>
            {children}
        </TableCell>
    </TableRow>
);

export const CollapsibleDetails = (props: React.PropsWithChildren<object>) => {
    const context = React.useContext(CollapsibleContext);
    const theme = useTheme();

    if (!context) {
        throw new Error("CollapsibleDetails have to be use inside CollapsibleContext");
    }

    return (
        <MinimalTableDetailsRow colSpan={12}>
            <Collapse in={context.open} collapsedSize={0} style={{ paddingLeft: theme.spacing(16) }}>
                <Box style={{ padding: theme.spacing(4), paddingTop: 0 }}>{props.children}</Box>
            </Collapse>
        </MinimalTableDetailsRow>
    );
};

export const CollapsibleRows = ({ children }: React.PropsWithChildren<object>) => {
    const context = React.useContext(CollapsibleContext);

    if (!context) {
        throw new Error("CollapsibleRows have to be used inside CollapsibleContext");
    }
    if (!context.open) {
        return null;
    }
    return <>{children}</>;
};
